import { motion } from 'framer-motion';
import { Mail, User } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT
import FooterNavigation from '../../../shared/components/Navigation';
import { PhoneFormInput, PhoneInputStyles, TextFormInput } from './FormInputs';
import { CustomerInfoType, hexToRgba, NodeSettings, useCustomerInfo } from '../../../shared/hooks/useCustomerInfo';

export interface StandardCustomerInfoProps {
  customerInfo: CustomerInfoType;
  setCustomerInfo: React.Dispatch<React.SetStateAction<CustomerInfoType>>;
  onNext: () => void;
  onPrev: () => void;
  nodeData?: {
    label: string;
    settings: NodeSettings;
  };
}

const StandardCustomerInfo: React.FC<StandardCustomerInfoProps> = ({
  customerInfo: initialCustomerInfo,
  setCustomerInfo: setParentCustomerInfo,
  onNext,
  onPrev,
  nodeData
}) => {
  const { t } = useTranslation(); // ADD THIS LINE

  // Use the custom hook
  const {
    customerInfo,
    errors,
    touched,
    handleChange,
    handlePhoneChange,
    handleBlur,
    isValid
  } = useCustomerInfo(initialCustomerInfo);

  // Sync local state with parent state
  React.useEffect(() => {
    setParentCustomerInfo(customerInfo);
  }, [customerInfo, setParentCustomerInfo]);

  // Default settings if nodeData is not provided
  const defaultSettings: NodeSettings = {
    requirePhone: true,
    requireEmail: true,
    showName: true,
    showLabels: true,
    showValidation: true,
    displayStyle: "standard",
    colors: {
      cardBackground: "#000000F2",
      cardBorder: "#DDB88919",
      cardBorderSelected: "#DDB889",
      cardShadow: "#DDB88919",
      cardShadowSelected: "#DDB88926",
      titleText: "#DDB889",
      subtitleText: "#DDB889B3",
      inputBackground: "#000000F2",
      inputBorder: "#DDB88933",
      inputText: "#DDB889",
      inputPlaceholder: "#DDB8894D",
      iconColor: "#DDB889B3",
      validationIconColor: "#22C55E",
      dividerColor: "#DDB88980",
      requiredFieldMark: "#F87171",
      selectionBackground: "#DDB889",
      selectionIcon: "#000000",
      badgeBackground: "#000000F2",
      badgeBorder: "#DDB88919",
      infoText: "#DDB889",
      buttonBackground: "#DDB889",
      buttonText: "#000000",
      placeholderGradientFrom: "#DDB88933",
      placeholderGradientTo: "#DDB8890D",
      placeholderIconBackground: "#DDB88933"
    }
  };

  // Use nodeData settings if available, otherwise use defaults
  const settings = (!nodeData?.settings || Object.keys(nodeData.settings).length === 0)
    ? defaultSettings
    : nodeData.settings;

  const { colors, showValidation, showName } = settings;

  const borderRgba = hexToRgba(colors.cardBorder);
  return (
    <motion.div
  className={`lp-w-full lp-rounded-2xl lp-p-2 lp-transition-all lp-duration-700
    lp-bg-[${colors.cardBackground}]
    lp-backdrop-blur-sm
    lp-shadow-[0_0_0.4px_${borderRgba},_0_0_15px_${colors.cardShadow}]
    hover:lp-shadow-[0_0_0.6px_${borderRgba},_0_0_20px_${colors.cardShadowSelected}]`}
  initial={{ opacity: 0, scale: 0.95 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ duration: 0.4 }}
    >
      <div className="lp-flex lp-items-center lp-justify-center lp-mb-6">
        <div className="lp-flex lp-items-center lp-space-x-4">
          <div className={`lp-h-[1px] lp-w-16 lp-bg-gradient-to-r lp-from-transparent lp-to-[${colors.dividerColor}]`}></div>
          <h3 className={`lp-text-2xl lp-font-medium lp-text-center lp-flex lp-items-center lp-text-[${colors.titleText}] lp-font-boska`}>
            <User className="lp-mr-2 lp-hidden md:lp-block" size={24} color={colors.titleText} />
            {nodeData?.label || t('booking.enterDetails')} {/* CHANGED: "Your Information" */}
          </h3>
          <div className={`lp-h-[1px] lp-w-16 lp-bg-gradient-to-l lp-from-transparent lp-to-[${colors.dividerColor}]`}></div>
        </div>
      </div>

      <div className="lp-space-y-8">
        {/* First Name Input - Only show if showName is true */}
        {showName && (
          <TextFormInput
            label={t('customerInfo.firstNameLabel')} /* CHANGED: "First Name" */
            name="firstName"
            placeholder={t('customerInfo.firstNamePlaceholder')} /* CHANGED: "First Name" */
            value={customerInfo.firstName}
            error={errors.firstName}
            touched={touched.firstName}
            required={true}
            icon={User}
            onChange={handleChange}
            onBlur={() => handleBlur('firstName')}
            colors={colors}
            showValidation={showValidation}
          />
        )}

        {/* Last Name Input - Only show if showName is true */}
        {showName && (
          <TextFormInput
            label={t('customerInfo.lastNameLabel')} /* CHANGED: "Last Name" */
            name="lastName"
            placeholder={t('customerInfo.lastNamePlaceholder')} /* CHANGED: "Last Name" */
            value={customerInfo.lastName}
            error={errors.lastName}
            touched={touched.lastName}
            required={true}
            icon={User}
            onChange={handleChange}
            onBlur={() => handleBlur('lastName')}
            colors={colors}
            showValidation={showValidation}
          />
        )}

        {/* Email Input - Only show if requireEmail is true */}
        {settings.requireEmail && (
          <TextFormInput
            label={t('customerInfo.emailLabel')} /* CHANGED: "Email" */
            name="email"
            placeholder={t('customerInfo.emailPlaceholder')} /* CHANGED: "Email" */
            value={customerInfo.email}
            error={errors.email}
            touched={touched.email}
            required={true}
            icon={Mail}
            onChange={handleChange}
            onBlur={() => handleBlur('email')}
            colors={colors}
            showValidation={showValidation}
            type="email"
          />
        )}

        {/* Phone Input - Only show if requirePhone is true */}
        {settings.requirePhone && (
          <PhoneFormInput
            label={t('customerInfo.phoneLabel')} /* CHANGED: "Phone Number" */
            name="phone"
            placeholder={t('customerInfo.phonePlaceholder')} /* CHANGED: "Phone Number" */
            value={customerInfo.phone}
            error={errors.phone}
            touched={touched.phone}
            required={true}
            icon={User}
            onChange={handlePhoneChange}
            onBlur={() => handleBlur('phone')}
            colors={colors}
            showValidation={showValidation}
          />
        )}
      </div>

      <div className='lp-mt-8'/>
      <FooterNavigation
        onNext={onNext}
        onPrev={onPrev}
        disabled={!isValid}
      />

      {/* Phone Input Styles with dynamic colors */}
      <PhoneInputStyles colors={colors} />
    </motion.div>
  );
};

export default StandardCustomerInfo;
